// Appreciation - a curated page for those who wish to send tokens of thanks.
// Add or remove wishlists in the WISHLISTS array below. Each entry:
//   { store, url, note, active }
// Set `active: false` for wishlists that aren't live yet - they'll show as "Coming soon"
// and won't be clickable.
import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Gift, Heart, Bitcoin, Copy, Check } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const GIFT_IMAGE =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/zuhbz2fk_IMG_7498.jpg";

const WISHLISTS = [
  {
    store: "Amazon UK",
    url: "https://www.amazon.co.uk/registry/wishlist/R91R37PWCYLB",
    note: "Curated everyday delights, essentials and objects of beauty.",
    active: true,
  },
  {
    store: "Throne",
    url: "",
    note: "Anonymous tributes handled through Throne.",
    active: false,
  },
  {
    store: "Cash Tribute",
    url: "",
    note: "Discreet direct arrangements by request.",
    active: false,
  },
];

// Gift cards - a lovely, low-friction way to send a token.
// Add `image` for a visual thumbnail. Set `active: false` for cards not yet live.
const GIFT_CARDS = [
  {
    store: "Uber",
    note: "For all the little journeys.",
    url: "https://www.uber.com/gift/",
    image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/74x9bgk1_uber.jpg",
    active: true,
  },
  {
    store: "Deliveroo",
    note: "Dinner, delivered.",
    url: "https://deliveroo.co.uk/gift-cards",
    image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/2pdnc4ok_Deliveroo%202.jpg",
    active: true,
  },
  {
    store: "Airbnb",
    note: "Somewhere quiet to disappear.",
    url: "https://www.airbnb.co.uk/gift-cards",
    image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/33n23yad_airbnb.jpg",
    active: true,
  },
  {
    store: "Netflix",
    note: "Cinema at home.",
    url: "https://www.netflix.com/gift-cards",
    image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/ogewqwab_netflix%202.jpg",
    active: true,
  },
  {
    store: "Tesco",
    note: "The everyday, made easier.",
    url: "https://secure.tesco.com/account/en-GB/gift-cards",
    image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/822pbkaw_tesco%20finest.png",
    active: true,
  },
  {
    store: "Asda",
    note: "For the practical & the pretty.",
    url: "https://gift-cards.asda.com",
    image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/ynybpt77_Asda.jpg",
    active: true,
  },
];

const KIND_WORDS = [
  {
    icon: Heart,
    title: "No obligation",
    text: "Gifts are never expected. Booking a session is always the first and best way to say thank you.",
  },
  {
    icon: Gift,
    title: "Delight, not devotion",
    text: "Tributes are received with grace, remembered fondly - and never bought influence.",
  },
];

export default function Appreciation() {
  usePageMeta({title:"Appreciation - Wishlist, Gift Cards & Tribute",description:"Show appreciation for Queen Cleopatra's work: Amazon UK wishlist, curated gift cards and cryptocurrency tribute options.",path:"/appreciation"});
  return (
    <div data-testid="page-appreciation">
      {/* Header */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">A Curated Page</div>
            <h1 className="font-serif text-5xl md:text-7xl text-cream leading-[1] mb-6">
              <em className="text-gold">Appreciation</em>.
            </h1>
            <p className="text-cream/70 leading-relaxed max-w-xl text-sm md:text-base">
              For those who wish to send a token of thanks - a lovely small ritual,
              held gently. Nothing here replaces the pleasure of a well-booked session,
              but a thoughtful gift is always received with grace.
            </p>
          </div>
          <div className="md:col-span-5">
            <figure className="relative">
              <div className="absolute inset-0 -translate-x-3 -translate-y-3 border border-[#c9a04b]/35 pointer-events-none" />
              <div className="relative aspect-[3/2] overflow-hidden border border-[#c9a04b]/25 bg-charcoalMuted">
                <img
                  src={GIFT_IMAGE}
                  alt="A curated arrangement - Chanel N°5, pearls, gold star, red heart, iPod and love-kiss gift bag"
                  className="w-full h-full object-cover"
                />
              </div>
            </figure>
          </div>
        </div>
      </section>

      {/* Wishlists */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-16">
        <div className="eyebrow mb-8">The Wishlists</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#c9a04b]/20" data-testid="wishlist-grid">
          {WISHLISTS.map(({ store, url, note, active }) => {
            const inner = (
              <>
                <div className="flex items-center justify-between mb-4">
                  <Gift size={20} className="text-gold" strokeWidth={1.4} />
                  {active ? (
                    <ExternalLink size={14} className="text-cream/40 group-hover:text-gold transition-colors" />
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Coming soon</span>
                  )}
                </div>
                <div className="font-serif text-2xl text-cream mb-2">{store}</div>
                <div className="text-sm text-cream/60 leading-relaxed">{note}</div>
                {active && (
                  <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-gold group-hover:text-cream transition-colors">
                    Open the list →
                  </div>
                )}
              </>
            );
            const baseClasses = "bg-charcoal p-8 md:p-10 flex flex-col min-h-[220px] border-t-2 transition-all";
            const key = store.toLowerCase().replace(/\s+/g, "-");
            return active ? (
              <a
                key={store}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`wishlist-${key}`}
                className={`${baseClasses} border-transparent hover:border-gold hover:bg-charcoalMuted group cursor-pointer`}
              >
                {inner}
              </a>
            ) : (
              <div
                key={store}
                data-testid={`wishlist-${key}-inactive`}
                className={`${baseClasses} border-transparent opacity-60`}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </section>

      {/* Gift cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="eyebrow mb-3">Gift Cards</div>
        <p className="text-sm text-cream/60 max-w-xl mb-10 leading-relaxed">
          A digital gift card is a low-friction, thoughtful gesture. Send at any denomination
          you wish; every one is received with grace.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="giftcard-grid">
          {GIFT_CARDS.map(({ store, note, url, image, active }) => {
            const key = store.toLowerCase().replace(/\s+/g, "-");
            const inner = (
              <>
                {/* Card image */}
                <div className="relative aspect-[8/5] bg-charcoalMuted overflow-hidden border border-[#c9a04b]/20 group-hover:border-gold transition-colors">
                  {image ? (
                    <img
                      src={image}
                      alt={`${store} gift card`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Gift size={28} className="text-gold/60" strokeWidth={1.3} />
                    </div>
                  )}
                  {active && (
                    <div className="absolute top-2 right-2 w-7 h-7 bg-charcoal/70 backdrop-blur border border-[#c9a04b]/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink size={12} className="text-gold" />
                    </div>
                  )}
                </div>
                {/* Meta */}
                <div className="mt-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-lg text-cream leading-tight">{store}</div>
                    <div className="text-xs text-cream/55 mt-1 leading-relaxed">{note}</div>
                  </div>
                  {!active && (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-cream/40 whitespace-nowrap mt-1">Soon</span>
                  )}
                </div>
              </>
            );
            return active ? (
              <a
                key={store}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`giftcard-${key}`}
                className="group block"
              >
                {inner}
              </a>
            ) : (
              <div key={store} data-testid={`giftcard-${key}-inactive`} className="opacity-60">{inner}</div>
            );
          })}
        </div>
        <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-cream/40">
          Please send digital gift card codes by email - details on the Contact page.
        </p>
      </section>

      {/* Bitcoin */}
      <BitcoinBlock />


      {/* Kind words */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="grid md:grid-cols-2 gap-px bg-[#c9a04b]/15">
          {KIND_WORDS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-charcoal p-8">
              <Icon size={20} className="text-gold mb-4" strokeWidth={1.4} />
              <div className="font-serif text-xl text-cream mb-2">{title}</div>
              <div className="text-sm text-cream/65 leading-relaxed">{text}</div>
            </div>
          ))}
        </div>

        <p className="mt-16 max-w-2xl text-xs text-cream/45 leading-relaxed tracking-wider">
          Adults 18+ · Tributes are never a substitute for booking a session and never confer
          privilege, priority or private communication. Received with grace - nothing more,
          nothing less.
        </p>
      </section>
    </div>
  );
}

// ------------------------------------------------------------
// Crypto - Bitcoin + USDT (ERC-20). To disable a coin, set its
// `address` to `null` and it will show as "Coming soon".
// ------------------------------------------------------------
const CRYPTO = [
  {
    id: "btc",
    name: "Bitcoin",
    network: "BTC · On-chain",
    address: "3NGfXQgpsqTUjg6REt8cQVa2vaVNVLYXrg",
    qr: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/3jbit0uo_coinbase%20bitcoin.png",
    warning: "Send Bitcoin (BTC) only. Confirmations typically clear within the hour.",
  },
  {
    id: "usdt",
    name: "USDT",
    network: "ERC-20 · Ethereum",
    address: "0xD6F55F814Bb5027805CF63C33DF775115c444cfE",
    qr: null,
    logo: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/hqm4uxs1_usdt.png",
    warning: "Please send only via the Ethereum (ERC-20) network. Sending on any other chain will result in permanent loss.",
  },
];

function BitcoinBlock() {
  const [copiedId, setCopiedId] = useState(null);

  const onCopy = async (id, address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedId(id);
      toast.success("Address copied to clipboard.");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
    } catch {
      toast.error("Couldn't copy - please long-press to select.");
    }
  };

  return (
    <section data-testid="crypto-block" className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
      <div className="eyebrow mb-3">Crypto</div>
      <p className="text-sm text-cream/60 max-w-xl mb-8 leading-relaxed">
        For discreet, network-native tributes. Copy an address below or scan the
        QR with your wallet app.
      </p>

      <div className="grid md:grid-cols-2 gap-px bg-[#c9a04b]/20">
        {CRYPTO.map((c) => (
          <div
            key={c.id}
            data-testid={`crypto-${c.id}`}
            className="bg-charcoal p-6 md:p-8 flex flex-col"
          >
            <div className="flex items-start gap-5 mb-5">
              <div className="w-14 h-14 bg-cream border border-[#c9a04b]/40 flex items-center justify-center shrink-0 overflow-hidden">
                {c.qr ? (
                  <img src={c.qr} alt={`${c.name} QR code`} className="w-full h-full object-contain p-1" />
                ) : c.logo ? (
                  <img src={c.logo} alt={`${c.name} logo`} className="w-8 h-8 object-contain" />
                ) : (
                  <Bitcoin size={26} className="text-charcoal" strokeWidth={1.5} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-2xl text-cream leading-none">{c.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold/85 mt-2">{c.network}</div>
              </div>
            </div>

            {c.address ? (
              <>
                <code
                  data-testid={`crypto-${c.id}-address`}
                  className="block bg-charcoalMuted border border-[#c9a04b]/25 text-cream/85 text-[11px] md:text-[12px] px-3 py-3 font-mono break-all mb-3"
                >
                  {c.address}
                </code>
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => onCopy(c.id, c.address)}
                    data-testid={`crypto-${c.id}-copy`}
                    className="btn-secondary inline-flex items-center justify-center gap-2 !py-2.5 !px-4 !text-[11px]"
                  >
                    {copiedId === c.id ? <Check size={13} /> : <Copy size={13} />}
                    {copiedId === c.id ? "Copied" : "Copy address"}
                  </button>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 hidden sm:block">
                    {c.qr ? "Or scan QR" : ""}
                  </div>
                </div>
                <p className="mt-4 text-[11px] text-cream/50 leading-relaxed">
                  {c.warning}
                </p>
              </>
            ) : (
              <div className="text-[11px] uppercase tracking-[0.22em] text-cream/45 border border-dashed border-[#c9a04b]/25 px-4 py-3 text-center">
                Address coming soon
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

